import { ValidationError } from 'components/ui/excel-table/types';
import { TFunction } from 'i18next';
import {
  LayerResponseDtoLimitTypeEnum,
  SyncLayerPayloadDto,
} from 'libs/pinnacle-sdk';
import { isInteger, isNil } from 'lodash';
import { LayerStructure } from './types';

export const validateLayer = (
  row: LayerStructure,
  rowIndex: number,
  allRows: LayerStructure[],
  t: TFunction
): ValidationError<LayerStructure>[] => {
  const errors: ValidationError<LayerStructure>[] = [];

  // Layer Number validation
  if (!row.layerNumber || row.layerNumber <= 0) {
    errors.push({
      field: 'layerNumber',
      rowIndex,
      message: t('layerStructure.validation.layerNumberRequired'),
    });
  }

  // Currency validation
  if (!row.currency) {
    errors.push({
      field: 'currency',
      rowIndex,
      message: t('layerStructure.validation.currencyRequired'),
    });
  }

  // Limit Type validation
  if (!row.limitType) {
    errors.push({
      field: 'limitType',
      rowIndex,
      message: t('layerStructure.validation.limitTypeRequired'),
    });
  }

  // Limit validation
  if (!row.limit || row.limit <= 0 || !isInteger(Number(row.limit))) {
    errors.push({
      field: 'limit',
      rowIndex,
      message: t('validation.wholePositiveField', {
        field: t('layerStructure.headers.limit'),
      }),
    });
  }

  if (row.limitType === LayerResponseDtoLimitTypeEnum.ExcessOfLoss) {
    if (!row.excess) {
      errors.push({
        field: 'excess',
        rowIndex,
        message: t('layerStructure.validation.excessRequired'),
      });
    }
  }

  if (
    !isNil(row.excess) &&
    (row.excess <= 0 || !isInteger(Number(row.excess)))
  ) {
    errors.push({
      field: 'excess',
      rowIndex,
      message: t('validation.wholePositiveField', {
        field: t('layerStructure.headers.excess'),
      }),
    });
  }

  // Deductibles validation
  if (
    row.deductibles &&
    (row.deductibles <= 0 || !isInteger(Number(row.deductibles)))
  ) {
    errors.push({
      field: 'deductibles',
      rowIndex,
      message: t('validation.wholePositiveField', {
        field: t('layerStructure.headers.deductibles'),
      }),
    });
  }

  // Layer Premium validation
  if (
    isNil(row.layerPremium) ||
    row.layerPremium < 0 ||
    !isInteger(Number(row.layerPremium))
  ) {
    errors.push({
      field: 'layerPremium',
      rowIndex,
      message: t('general.nonNegative', {
        field: t('layerStructure.headers.layerPremium'),
      }),
    });
  }

  // Total Deductions validation
  if (
    isNil(row.totalDeductions) ||
    row.totalDeductions < 0 ||
    row.totalDeductions > 20
  ) {
    errors.push({
      field: 'totalDeductions',
      rowIndex,
      message: t('layerStructure.validation.totalDeductionsRange'),
    });
  }

  // Line Size validation
  if (
    (!row.lineSize && row.lineSize != 0) ||
    row.lineSize < 0 ||
    row.lineSize > 100
  ) {
    errors.push({
      field: 'lineSize',
      rowIndex,
      message: t('layerStructure.validation.lineSizeRange'),
    });
  }

  // Empty row validation
  const isEmptyRow = !Object.values(row).some(
    (value) => value !== undefined && value !== null && value !== ''
  );
  if (isEmptyRow) {
    errors.push({
      field: 'layerNumber',
      rowIndex,
      message: t('layerStructure.validation.emptyRow'),
    });
  }

  return errors;
};

export const validateLayerCount = (
  data: LayerStructure[],
  t: TFunction
): ValidationError<LayerStructure>[] => {
  const errors: ValidationError<LayerStructure>[] = [];

  if (data.length > 10) {
    errors.push({
      field: 'layerNumber',
      rowIndex: 10,
      message: t('layerStructure.validation.maxLayers'),
    });
  }

  if (data.length === 0) {
    errors.push({
      field: 'layerNumber',
      rowIndex: 0,
      message: t('layerStructure.validation.minLayers'),
    });
  }

  return errors;
};

export const convertLayerStructureToPayload = (
  rows: LayerStructure[]
): SyncLayerPayloadDto[] => {
  return rows.map((row) => ({
    layerNumber: row.layerNumber,
    currency: row.currency,
    limitType: row.limitType,
    limit: String(row.limit),
    excess: !isNil(row.excess) ? String(row.excess) : null,
    deductibles: !isNil(row.deductibles) ? String(row.deductibles) : null,
    premium: !isNil(row.layerPremium) ? String(row.layerPremium) : null,
    totalDeductions: !isNil(row.totalDeductions)
      ? String(row.totalDeductions)
      : null,
    lineSize: !isNil(row.lineSize) ? String(row.lineSize) : null,
    layerId: row.layerId,
  })) as SyncLayerPayloadDto[];
};
